import React, { useState } from 'react'
import { Users, Clock, Trophy, X, ChevronDown, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  useShiftReplacementRequests,
  useCancelShiftReplacement,
  type ShiftReplacementRequest,
  type ShiftReplacementStatus,
  type OfferStatus,
} from '@/hooks/schedules/useShiftReplacement'

const STATUS_LABELS: Record<ShiftReplacementStatus, string> = {
  open: 'Идёт подбор',
  filled: 'Закрыта',
  exhausted: 'Не найдена',
  cancelled: 'Отменена',
}

const STATUS_CLASSES: Record<ShiftReplacementStatus, string> = {
  open: 'bg-blue-100 text-blue-700',
  filled: 'bg-green-100 text-green-700',
  exhausted: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-gray-100 text-gray-600',
}

const OFFER_LABELS: Record<OfferStatus, string> = {
  pending: 'Ждёт ответа',
  accepted: 'Принял',
  declined: 'Отказался',
  expired: 'Истёк',
}

const OFFER_CLASSES: Record<OfferStatus, string> = {
  pending: 'bg-blue-50 text-blue-600',
  accepted: 'bg-green-50 text-green-600',
  declined: 'bg-red-50 text-red-600',
  expired: 'bg-gray-50 text-gray-500',
}

const FILTERS: Array<{ value: ShiftReplacementStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'open', label: 'Идёт подбор' },
  { value: 'filled', label: 'Закрытые' },
  { value: 'exhausted', label: 'Не найдены' },
  { value: 'cancelled', label: 'Отменённые' },
]

function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const RequestCard: React.FC<{ request: ShiftReplacementRequest }> = ({
  request,
}) => {
  const [expanded, setExpanded] = useState(request.status === 'open')
  const cancel = useCancelShiftReplacement()

  const absentName = request.absent_employee
    ? `${request.absent_employee.last_name} ${request.absent_employee.first_name}`
    : '—'
  const currentOffer = request.offers.find((o) => o.status === 'pending')
  const acceptedOffer = request.offers.find((o) => o.status === 'accepted')

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <button
            className="flex items-center gap-2 text-left"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            )}
            <div>
              <CardTitle className="text-base">
                {request.positions?.name ?? 'Должность'} · {request.date}
              </CardTitle>
              <div className="mt-0.5 text-sm text-muted-foreground">
                Замена за {absentName} · {request.locations?.name ?? ''}
              </div>
            </div>
          </button>
          <div className="flex items-center gap-2">
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_CLASSES[request.status]}`}
            >
              {STATUS_LABELS[request.status]}
            </span>
            {request.status === 'open' && (
              <Button
                variant="ghost"
                size="sm"
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(request.id)}
                title="Отменить заявку"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Trophy className="h-3.5 w-3.5" />
            {request.base_xp}→{request.xp_cap} XP (+{request.xp_step}/шаг)
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            оффер {request.offer_ttl_min} мин · кулдаун {request.cooldown_min} мин
          </span>
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            офферов: {request.offers.length}
          </span>
          {currentOffer && (
            <span className="font-medium text-blue-600">
              сейчас: +{currentOffer.offered_xp} XP, до {fmtTime(currentOffer.respond_by)}
            </span>
          )}
          {acceptedOffer && (
            <span className="font-medium text-green-600">
              принял: +{acceptedOffer.offered_xp} XP
            </span>
          )}
        </div>
      </CardHeader>

      {expanded && request.offers.length > 0 && (
        <CardContent className="pt-0">
          <div className="space-y-1">
            {request.offers.map((offer) => (
              <div
                key={offer.id}
                className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">#{offer.rank_index + 1}</span>
                  <span>
                    {offer.employees
                      ? `${offer.employees.last_name} ${offer.employees.first_name}`
                      : offer.employee_id.slice(0, 8)}
                  </span>
                  <span className="font-medium text-amber-600">
                    +{offer.offered_xp} XP
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{fmtTime(offer.sent_at)}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 font-medium ${OFFER_CLASSES[offer.status]}`}
                  >
                    {OFFER_LABELS[offer.status]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  )
}

export const ShiftReplacements: React.FC = () => {
  const [filter, setFilter] = useState<ShiftReplacementStatus | 'all'>('all')
  const { data: requests, isLoading } = useShiftReplacementRequests(
    filter === 'all' ? undefined : filter,
  )

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Users className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-semibold">Биржа замен смен</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Заявки создаются в графике: Графики → По отделам → ячейка смены
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground">Загрузка…</div>
      ) : !requests || requests.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground">
          Заявок на замену нет
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <RequestCard key={r.id} request={r} />
          ))}
        </div>
      )}
    </div>
  )
}

export default ShiftReplacements
